import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  ParkingClientPayload,
  ParkingClientTableQuery,
  ParkingClientTableResponse,
} from '../../../models/parking/parking-client.model';

@Injectable({ providedIn: 'root' })
export class ApiParkingClientService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  private get token(): string {
    return this.cookieService.get('token');
  }

  private get userId(): number {
    return Number(this.cookieService.get('user_id') || 0);
  }

  getForTable(query: ParkingClientTableQuery): Observable<ParkingClientTableResponse> {
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: this.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.clientTypeId) params['clientTypeId'] = query.clientTypeId;
    if (query.clientRole) params['clientRole'] = query.clientRole;

    return this.apiMethodService.request('/ParkingClient/getForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingClientForTable',
      token: this.token,
      headerOnly: false,
      params,
    });
  }

  getById(id: number): Observable<unknown> {
    return this.apiMethodService.request('/ParkingClient/getById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingClientById',
      token: this.token,
      headerOnly: false,
      params: { id, userId: this.userId },
    });
  }

  getClientTypeOptions(siteId: number): Observable<unknown> {
    return this.apiMethodService.request('/ParkingClient/getClientTypeOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingClientTypeOptions',
      token: this.token,
      headerOnly: false,
      params: { siteId },
    });
  }

  getParentClientOptions(siteId: number, excludeId?: number): Observable<unknown> {
    const params: Record<string, string | number> = { siteId };
    if (excludeId) params['excludeId'] = excludeId;
    return this.apiMethodService.request('/ParkingClient/getParentClientOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingParentClientOptions',
      token: this.token,
      headerOnly: false,
      params,
    });
  }

  insert(body: ParkingClientPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/ParkingClient/insert', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'insertParkingClient',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  update(id: number, body: ParkingClientPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request(`/ParkingClient/update/${id}`, 'PATCH', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateParkingClient',
      token: this.token,
      headerOnly: false,
      body,
    });
  }
}
